/**
 * The Apps tab's API for one service: every endpoint the API tab found for it, and a small "Try it" that sends one
 * request as a visitor through vfs-service (`POST /api/send`, the API tab's own call and helpers: requestFor,
 * sendInput, prettyBody), with the status and the start of the body. Writes need an editor (`canWrite`), as on the
 * API tab. Signing in as an app user, headers and history stay on the full tab ("Open API").
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getEndpoints, sendRequest } from '../../../services/ApiTesterService';
import {
  METHODS, formatSize, isReadMethod, methodClass, prettyBody, requestFor, sendInput, statusName, statusTone,
} from '../../../utils/apiTesterModel';
import { sameService } from '../../../utils/serviceTabsModel';
import { errorText } from '../../data/DataCommon';
import { Card, LoadState, OpenLink, useLoaded } from './common';

const PREVIEW_CHARS = 1500;

export default function ApiTab({ projectId, service, onOpen = null }) {
  const state = useLoaded(() => getEndpoints(projectId), [projectId]);
  const entry = (state.value?.services || []).find((item) => sameService(item.service, service)) || null;
  const endpoints = entry?.endpoints || [];
  const canWrite = Boolean(state.value?.canWrite);
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('/');
  const [picked, setPicked] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const pick = (endpoint) => {
    setPicked(endpoint);
    setMethod(String(endpoint.method || 'GET').toUpperCase());
    setPath(endpoint.path || '/');
    setResult(null);
  };
  const blocked = !canWrite && !isReadMethod(method);
  const send = async(event) => {
    event.preventDefault();
    if (!entry || sending || blocked) { return; }
    // The endpoint's example body when it is the one picked; the path as typed (its {params} filled by hand).
    const base = requestFor(entry.service, picked && picked.method === method ? picked : { method, path });
    setSending(true);
    try {
      const answer = await sendRequest(projectId, { ...sendInput({ ...base, method, path, params: {} }), as: { kind: 'visitor' } });
      setResult({ answer });
    } catch (error) {
      setResult({ error: errorText(error) });
    }
    setSending(false);
  };
  const body = result?.answer ? prettyBody(result.answer) : null;

  return (
    <div className="apps-tab-body" data-testid="apps-tabpanel-api">
      <div className="apps-infos">
        <Card testId="apps-api-endpoints" title={endpoints.length ? `Endpoints · ${endpoints.length}` : 'Endpoints'}>
          {state.loading || state.error || endpoints.length === 0 ? <LoadState empty="No endpoints found for this service." state={state} /> : (
            <ul className="apps-endpoints is-picker">
              {endpoints.map((endpoint) => {
                const key = `${endpoint.method} ${endpoint.path}`;
                return (
                  <li key={key}>
                    <button aria-pressed={picked === endpoint} className="apps-endpoint" onClick={() => pick(endpoint)} title={endpoint.summary || undefined} type="button">
                      <strong className={`apps-method ${methodClass(endpoint.method)}`}>{endpoint.method}</strong> {endpoint.path}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        <Card testId="apps-api-try" title="Try it">
          {!entry ? <p className="apps-muted">{state.loading ? 'Loading…' : 'Nothing to call: the service has no endpoints listed.'}</p> : (
            <form className="apps-try" onSubmit={send}>
              <div className="apps-try-row">
                <select aria-label="Method" onChange={(event) => setMethod(event.target.value)} value={method}>
                  {METHODS.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
                <input aria-label="Path" onChange={(event) => setPath(event.target.value)} value={path} />
                <button className="apps-try-send" disabled={sending || blocked} type="submit">{sending ? 'Sending…' : 'Send'}</button>
              </div>
              {blocked && <p className="apps-muted apps-small">Only editors can send {method} requests.</p>}
              {entry.running === false &&<p className="apps-muted apps-small">The service is not running: start it first.</p>}
              {result?.error && <p className="apps-bad" role="alert">{result.error}</p>}
              {result?.answer && (
                <div className="apps-try-answer" data-testid="apps-api-answer">
                  <p>
                    <strong className={`apps-status is-${statusTone(result.answer.status)}`}>{result.answer.status} {statusName(result.answer.status, result.answer.statusText)}</strong>
                    <span className="apps-muted"> · {result.answer.ms ?? '—'} ms · {formatSize(result.answer.size)}</span>
                  </p>
                  {body.text && <pre className="apps-pre">{body.text.length > PREVIEW_CHARS ? `${body.text.slice(0, PREVIEW_CHARS)}\n…` : body.text}</pre>}
                </div>
              )}
            </form>
          )}
        </Card>
      </div>
      <OpenLink label="Open API" onOpen={onOpen} testId="apps-open-api" />
    </div>
  );
}

ApiTab.propTypes = { onOpen: PropTypes.func, projectId: PropTypes.string, service: PropTypes.string.isRequired };
